import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CheckSquare,
  Clock,
  FolderOpen,
  History,
  Pencil,
  Receipt,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import type { LucideIcon } from 'lucide-react';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import { errorMessage } from '../../../lib/errorMessage';
import { getValidTargets } from '../../../lib/lifecycle';
import { useCompanies } from '../../crm/companies/companiesQueries';
import { useProductLines } from '../../customers/productLinesQueries';
import { ProjectActivityTab } from './ProjectActivityTab';
import { ProjectDrawer } from './ProjectDrawer';
import { ProjectFinanceTab } from './ProjectFinanceTab';
import { ProjectHoursTab } from './ProjectHoursTab';
import { ProjectTasksTab } from './ProjectTasksTab';
import {
  PROJECT_STATUS_LABELS_HE,
  projectTransitionLabel,
  type ProjectRow,
  type ProjectStatus,
} from './projects.schema';
import {
  useChangeProjectStatus,
  useProject,
  useProjects,
  useSoftDeleteProject,
} from './projectsQueries';
import styles from './projects.module.css';

type Tab = 'overview' | 'tasks' | 'hours' | 'finance' | 'files' | 'activity';

const TABS: { id: Tab; label: string; icon: LucideIcon }[] = [
  { id: 'overview', label: 'סקירה', icon: Briefcase },
  { id: 'tasks', label: 'משימות', icon: CheckSquare },
  { id: 'hours', label: 'שעות', icon: Clock },
  { id: 'finance', label: 'כספים', icon: Receipt },
  { id: 'files', label: 'קבצים', icon: FolderOpen },
  { id: 'activity', label: 'פעילות', icon: History },
];

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t.id === value);
}

function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(amount)}`;
}

function formatDate(iso: string | null): string {
  return iso ? format(new Date(iso), 'dd/MM/yy', { locale: he }) : '—';
}

function StatusActions({ project, orgId }: { project: ProjectRow; orgId: string }) {
  const toast = useToast();
  const change = useChangeProjectStatus(orgId);
  const targets = getValidTargets('project', project.status).filter(
    (to): to is ProjectStatus => to !== project.status && to in PROJECT_STATUS_LABELS_HE,
  );

  async function move(to: ProjectStatus) {
    if (to === 'cancelled' && !window.confirm(`לבטל את ${project.name}?`)) return;
    try {
      await change.mutateAsync({ id: project.id, to });
      toast.success(`הסטטוס עודכן: ${PROJECT_STATUS_LABELS_HE[to]}`);
    } catch (err) {
      toast.error(`לא הצלחנו לשנות סטטוס · ${errorMessage(err)}`);
    }
  }

  if (targets.length === 0) return null;

  return (
    <div className={styles.statusActions}>
      <span className={styles.statusActionsLabel}>שנה סטטוס:</span>
      {targets.map((to) => (
        <button
          key={to}
          type="button"
          className={cn(
            to === 'cancelled' ? styles.btnDanger : styles.btnSecondary,
            styles.btnSmall,
          )}
          disabled={change.isPending}
          onClick={() => {
            void move(to);
          }}
        >
          {projectTransitionLabel(project.status, to)}
        </button>
      ))}
    </div>
  );
}

export function ProjectWorkspace() {
  const { id: projectId } = useParams<{ id: string }>();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const navigate = useNavigate();
  const toast = useToast();

  const { data: project, isLoading, error } = useProject(orgId, projectId ?? null);
  const { data: companies = [] } = useCompanies(orgId);
  const { data: projects = [] } = useProjects(orgId);
  const { data: productLines = [] } = useProductLines(orgId);
  const softDelete = useSoftDeleteProject(orgId);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'overview';
  const [editOpen, setEditOpen] = useState(false);

  const companyOptions = useMemo(
    () => companies.map((c) => ({ id: c.id, name: c.name })),
    [companies],
  );
  const projectOptions = useMemo(
    () => projects.map((p) => ({ id: p.id, name: p.name })),
    [projects],
  );

  function selectTab(next: Tab) {
    const nextParams = new URLSearchParams(params);
    if (next === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  }

  async function handleDelete() {
    if (!project) return;
    if (!window.confirm(`למחוק את ${project.name}? המשימות והחשבוניות שלו יימחקו איתו.`)) return;
    try {
      await softDelete.mutateAsync(project.id);
      toast.success('הפרויקט נמחק');
      navigate(project.company_id ? `/customers/${project.company_id}` : '/projects');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  if (isLoading || !orgId) return <p className={styles.notice}>טוען...</p>;
  if (error)
    return <p className={styles.notice}>לא הצלחנו לטעון את הפרויקט · {errorMessage(error)}</p>;
  if (!project) {
    return (
      <div className={styles.workspaceEmpty}>
        <p>הפרויקט לא נמצא.</p>
        <Link to="/projects" className={styles.btnSecondary}>
          חזור לפרויקטים
        </Link>
      </div>
    );
  }

  const customer = companies.find((c) => c.id === project.company_id) ?? null;
  const line = productLines.find((l) => l.id === project.product_line_id) ?? null;

  return (
    <div className={styles.workspace}>
      <header className={styles.workspaceHead}>
        <div className={styles.workspaceBreadcrumb}>
          {customer ? (
            <>
              <Link to={`/customers/${customer.id}?tab=projects`} className={styles.crumb}>
                <ArrowRight size={14} aria-hidden="true" />
                {customer.name}
              </Link>
              {line ? (
                <>
                  <span className={styles.crumbSep} aria-hidden="true">
                    ›
                  </span>
                  <span className={styles.crumb}>{line.name}</span>
                </>
              ) : null}
            </>
          ) : (
            <Link to="/projects" className={styles.crumb}>
              <ArrowRight size={14} aria-hidden="true" />
              פרויקטים
            </Link>
          )}
        </div>

        <div className={styles.workspaceTop}>
          <h1 className={styles.workspaceTitle}>{project.name}</h1>
          <div className={styles.workspaceActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setEditOpen(true);
              }}
            >
              <Pencil size={14} aria-hidden="true" />
              ערוך
            </button>
            <button
              type="button"
              className={styles.btnDanger}
              disabled={softDelete.isPending}
              onClick={() => {
                void handleDelete();
              }}
            >
              <Trash2 size={14} aria-hidden="true" />
              מחק
            </button>
          </div>
        </div>

        <div className={styles.workspaceMeta}>
          <span className={cn(styles.statusBadge, styles[`status--${project.status}`])}>
            {PROJECT_STATUS_LABELS_HE[project.status]}
          </span>
          {customer ? (
            <span>
              · לקוח: <strong>{customer.name}</strong>
            </span>
          ) : null}
          <span>
            · ערך: <bdi>{formatILS(project.value_amount)}</bdi>
          </span>
          {project.expected_end_date ? (
            <span>
              · יעד: <bdi>{formatDate(project.expected_end_date)}</bdi>
            </span>
          ) : null}
        </div>

        <StatusActions project={project} orgId={orgId} />
      </header>

      <nav className={styles.tabs} aria-label="טאבים של הפרויקט">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              className={cn(styles.tab, tab === t.id && styles.tabActive)}
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => {
                selectTab(t.id);
              }}
            >
              <Icon size={14} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className={styles.tabContent}>
        {tab === 'overview' ? (
          <div className={styles.overview}>
            <div className={styles.overviewGrid}>
              <div className={styles.kpi}>
                <span className={styles.kpiLabel}>סטטוס</span>
                <span className={styles.kpiValue}>{PROJECT_STATUS_LABELS_HE[project.status]}</span>
              </div>
              <div className={styles.kpi}>
                <span className={styles.kpiLabel}>ערך פרויקט</span>
                <span className={styles.kpiValue}>
                  <bdi>{formatILS(project.value_amount)}</bdi>
                </span>
              </div>
              <div className={styles.kpi}>
                <span className={styles.kpiLabel}>תעריף לשעה</span>
                <span className={styles.kpiValue}>
                  <bdi>{project.hourly_rate !== null ? formatILS(project.hourly_rate) : '—'}</bdi>
                </span>
              </div>
              <div className={styles.kpi}>
                <span className={styles.kpiLabel}>התחיל</span>
                <span className={styles.kpiValue}>
                  <bdi>{formatDate(project.started_at)}</bdi>
                </span>
              </div>
              <div className={styles.kpi}>
                <span className={styles.kpiLabel}>יעד סיום</span>
                <span className={styles.kpiValue}>
                  <bdi>{formatDate(project.expected_end_date)}</bdi>
                </span>
              </div>
            </div>

            <div className={styles.notesCard}>
              <h3>הערות</h3>
              {project.notes ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{project.notes}</p>
              ) : (
                <p className={styles.notice}>
                  אין הערות.{' '}
                  <button
                    type="button"
                    className={cn(styles.btnSecondary, styles.btnSmall)}
                    onClick={() => {
                      setEditOpen(true);
                    }}
                  >
                    הוסף הערה
                  </button>
                </p>
              )}
            </div>

            {project.source_lead_id ? (
              <Link to={`/leads/${project.source_lead_id}`} className={styles.linkOut}>
                ליד מקור <ArrowLeft size={14} aria-hidden="true" />
              </Link>
            ) : null}
          </div>
        ) : null}

        {tab === 'tasks' ? (
          <ProjectTasksTab
            project={project}
            projectOptions={projectOptions}
            orgId={orgId}
            userId={userId}
          />
        ) : null}

        {tab === 'hours' ? (
          <ProjectHoursTab
            project={project}
            projectOptions={projectOptions}
            orgId={orgId}
            userId={userId}
          />
        ) : null}

        {tab === 'finance' ? (
          <ProjectFinanceTab
            project={project}
            orgId={orgId}
            userId={userId}
            onEditProject={() => {
              setEditOpen(true);
            }}
          />
        ) : null}

        {tab === 'files' ? (
          <div className={styles.tabPlaceholder}>
            <FolderOpen size={48} aria-hidden="true" />
            <p>קבצים לפרויקט עדיין לא נתמכים. עד אז, שמור קישור לתיקייה בהערות הפרויקט.</p>
          </div>
        ) : null}

        {tab === 'activity' ? <ProjectActivityTab orgId={orgId} projectId={project.id} /> : null}
      </div>

      <ProjectDrawer
        open={editOpen}
        project={project}
        companies={companyOptions}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setEditOpen(false);
        }}
        onDeleted={() => {
          navigate(project.company_id ? `/customers/${project.company_id}` : '/projects');
        }}
      />
    </div>
  );
}
